import type { PhraseAssemblyParams, PhraseAssemblyState } from '../types';

interface Props {
  params: PhraseAssemblyParams;
  puzzleState: PhraseAssemblyState;
  onSolve: () => void;
  patchState: (updater: (prev: PhraseAssemblyState) => PhraseAssemblyState) => void;
}

export function PhraseAssembly({ params, puzzleState, onSolve, patchState }: Props) {
  const { words, correctPhrase } = params;
  const { wordSelection, solved } = puzzleState;

  function addWord(word: string) {
    if (solved) return;
    patchState(s => {
      const nextSel = [...s.wordSelection, word];
      const phrase = nextSel.join(' ').toLowerCase();
      if (phrase === correctPhrase) {
        onSolve();
        return { ...s, wordSelection: nextSel, solved: true };
      }
      return { ...s, wordSelection: nextSel };
    });
  }

  function removeWord(index: number) {
    if (solved) return;
    patchState(s => {
      const nextSel = s.wordSelection.filter((_, i) => i !== index);
      return { ...s, wordSelection: nextSel };
    });
  }

  function clearAll() {
    if (solved) return;
    patchState(s => ({ ...s, wordSelection: [] }));
  }

  // Words still available to click (can be used multiple times unless already placed max count)
  const usedCounts: Record<string, number> = {};
  wordSelection.forEach(w => { usedCounts[w] = (usedCounts[w] ?? 0) + 1; });
  const wordCounts: Record<string, number> = {};
  words.forEach(w => { wordCounts[w] = (wordCounts[w] ?? 0) + 1; });
  const available = words.filter(w => (usedCounts[w] ?? 0) < (wordCounts[w] ?? 1));

  if (solved) {
    return (
      <p className="puzzle-solved-msg">
        <i className="fa-solid fa-circle-check" aria-hidden="true" /> Phrase assembled.
      </p>
    );
  }

  return (
    <div className="puzzle-phrase">
      <p className="puzzle-narrative">
        Select words in order to build the phrase. Click a placed word to remove it.
      </p>

      {/* Built phrase display */}
      <div className="puzzle-phrase__built">
        {wordSelection.length === 0 ? (
          <span className="puzzle-phrase__placeholder">Click words below to start building…</span>
        ) : (
          wordSelection.map((word, i) => (
            <button
              key={i}
              className="puzzle-phrase__word puzzle-phrase__word--placed"
              type="button"
              onClick={() => removeWord(i)}
              aria-label={`Remove "${word}"`}
            >
              {word}
            </button>
          ))
        )}
      </div>

      {wordSelection.length > 0 && (
        <button
          className="escape-btn escape-btn--ghost escape-btn--sm"
          type="button"
          onClick={clearAll}
        >
          <i className="fa-solid fa-rotate-left" aria-hidden="true" /> Clear
        </button>
      )}

      {/* Word bank */}
      <div className="puzzle-phrase__bank">
        {words.map((word, i) => {
          const isUsed = !available.includes(word);
          return (
            <button
              key={`${word}-${i}`}
              className={`puzzle-phrase__word${isUsed ? ' puzzle-phrase__word--used' : ''}`}
              type="button"
              disabled={isUsed}
              onClick={() => !isUsed && addWord(word)}
              aria-label={`Add "${word}"`}
            >
              {word}
            </button>
          );
        })}
      </div>
    </div>
  );
}
